/**
 * useTaskPhases - Hook para gerenciamento de fases de tarefas
 * 
 * Fornece queries e mutations para CRUD de fases via RPCs server-side.
 * Fases são vinculadas a uma categoria.
 * 
 * @version 1.3.0
 * RELEASE: 2026-10-11 v1.4.0 - Novo parâmetro opcional serviceOrdersOverride: telas da O.S. abertas fora
 *   das rotas /tenant/service-orders (ex.: modal Veículo/Cliente) usam fases da O.S.
 * RELEASE: 2026-10-08 v1.3.0 - Rotas da Ordem de Serviço usam as RPCs *_service_order_phase(s); reorderPhases.
 * RELEASE: 2026-04-06 v1.0.0 - Criação do hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useTaskModuleType, useIsServiceOrderStructure } from '@/hooks/useTaskModuleType';

// Logger para depuração
const logger = createLogger('useTaskPhases');

// Tipo da fase retornada pela RPC
export interface TaskPhase {
  id: string;
  name: string;
  description: string;
  color: string;
  sort_order: number;
  is_final: boolean;
  category_id: string;
  category_name: string;
  category_color: string;
  tasks_count: number;
  created_at: string;
  updated_at: string;
  /** v1.1.0: marcador "Padrão" da categoria (opcional) */
  is_default?: boolean;
}

// Tipo para criação/atualização de fase
export interface UpsertTaskPhaseInput {
  p_id?: string;
  p_category_id: string;
  p_name: string;
  p_description?: string;
  p_color?: string;
  p_sort_order?: number;
  p_is_final?: boolean;
  /** v1.1.0: marca a fase como padrão da categoria (opcional) */
  p_is_default?: boolean;
}

/**
 * Hook principal para fases de tarefas
 * @param categoryId - Filtro opcional por categoria
 * @param taskTypeOverride - (v1.2.0) Força o módulo em vez de derivar da rota
 */
export function useTaskPhases(
  categoryId?: string,
  taskTypeOverride?: 'normal' | 'retention' | 'legal',
  /** Força fases da O.S. independentemente da rota (v1.4.0) */
  serviceOrdersOverride: boolean = false,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  // Módulo corrente (normal | retention | legal) derivado da rota ou override
  const routeTaskType = useTaskModuleType();
  const taskType = taskTypeOverride ?? routeTaskType;
  // v1.3.0: Ordem de Serviço usa as mesmas telas com RPCs próprias
  const routeIsOS = useIsServiceOrderStructure();
  const isOS = serviceOrdersOverride || (routeIsOS && !taskTypeOverride);
  const tenantHint = currentTenant?.id ?? null;

  // Query para listar fases
  const phasesQuery = useQuery({
    queryKey: ['task-phases', currentTenant?.id, categoryId, isOS ? 'service_order' : taskType],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const params: Record<string, unknown> = { p_task_type: taskType };
      if (categoryId) params.p_category_id = categoryId;

      const { data, error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'get_service_order_phases', {
            p_category_id: categoryId ?? null,
            p_tenant_hint: tenantHint,
          })
        : await supabase.rpc('get_task_phases', params as any);

      if (error) {
        logger.error('Erro ao buscar fases de tarefas', { error: error.message });
        throw error;
      }

      return (data || []) as TaskPhase[];
    },
    enabled: !!currentTenant?.id,
  });

  // Mutation para criar/atualizar fase
  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertTaskPhaseInput) => {
      const { data, error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'upsert_service_order_phase', { ...input, p_tenant_hint: tenantHint })
        : await supabase.rpc('upsert_task_phase', input);

      if (error) {
        logger.error('Erro ao salvar fase de tarefa', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['task-phases'] });
      toast.success(variables.p_id ? 'Fase atualizada com sucesso' : 'Fase criada com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar fase: ${error.message}`);
    },
  });

  // Mutation para excluir fase
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'delete_service_order_phase', { p_id: id, p_tenant_hint: tenantHint })
        : await supabase.rpc('delete_task_phase', { p_id: id });

      if (error) {
        logger.error('Erro ao excluir fase de tarefa', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-phases'] });
      toast.success('Fase excluída com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir fase: ${error.message}`);
    },
  });

  /**
   * v1.3.0 — Persiste a ordem das fases de uma categoria.
   * Tarefas: reaproveita upsert_task_phase fase a fase (comportamento anterior).
   * OS: uma única chamada atômica a reorder_service_order_phases.
   */
  const reorderPhases = async (catId: string, ordered: TaskPhase[]) => {
    if (isOS) {
      const { error } = await (supabase.rpc as any).call(supabase, 'reorder_service_order_phases', {
        p_category_id: catId,
        p_phase_ids: ordered.map((p) => p.id),
        p_tenant_hint: tenantHint,
      });
      if (error) throw error;
      return;
    }
    for (let i = 0; i < ordered.length; i++) {
      const p = ordered[i];
      if (p.sort_order === i + 1) continue;
      const { error } = await supabase.rpc('upsert_task_phase', {
        p_id: p.id,
        p_category_id: catId,
        p_name: p.name,
        p_description: p.description || undefined,
        p_color: p.color,
        p_sort_order: i + 1,
        p_is_final: p.is_final,
        p_is_default: !!p.is_default,
      });
      if (error) throw error;
    }
  };

  return {
    reorderPhases,
    phases: phasesQuery.data || [],
    isLoading: phasesQuery.isLoading,
    error: phasesQuery.error,
    refetch: phasesQuery.refetch,
    upsert: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}
